import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ROUTES from '@/ROUTES'

import { IOrganization } from '@/MODELS/organization.model'

import { api_delete } from '@/services/api.service'
import { useResource } from '@/services/resource.service'

import AdminPageHeader from '@/components/AdminPageHeader'
import Button from '@/components/Button'
import ConfirmModal from '@/components/ConfirmModal'
import Table from '@/components/Table'

// Admin row type adds the fields the GET list endpoint enriches with.
type OrganizationRow = IOrganization & { seatsUsed: number; memberCount: number }

const TYPE_LABELS: Record<IOrganization['type'], string> = {
    dd_board: 'DD Board',
    early_intervention: 'Early Intervention',
    clinic: 'Clinic',
    school: 'School',
    nonprofit: 'Nonprofit',
    other: 'Other',
}

export default function AdminOrganizationsTable() {
    const navigate = useNavigate()
    const [organizations, , orgResource] = useResource<OrganizationRow[]>(ROUTES.admin.organization)
    const [deleteId, setDeleteId] = useState<string | null>(null)
    const deleteTarget = organizations?.find((o) => o._id === deleteId)

    async function handleDelete() {
        if (!deleteId) return
        await api_delete(`${ROUTES.admin.organization}/${deleteId}`)
        setDeleteId(null)
        await orgResource.get()
    }

    return (
        <div className='flex flex-col w-full gap-5 py-10 mx-auto max-w-7xl'>
            <AdminPageHeader
                heading='Organizations'
                subheading='Sponsoring partners and their seat quotas. Click "Edit & Team" to manage coordinators and family assignments.'
                className='py-5'
                action={<Button onClick={() => navigate('/admin/organization/new')}>Add Organization</Button>}
            />
            <Table<OrganizationRow>
                data={organizations ?? []}
                columns={[
                    {
                        header: 'Name',
                        accessor: 'name',
                        cell: (org) => (
                            <div className='flex flex-col'>
                                <span className='font-medium'>{org.name}</span>
                                {org.displayName && org.displayName !== org.name && (
                                    <span className='text-xs text-gray-500'>shown to families: {org.displayName}</span>
                                )}
                            </div>
                        ),
                    },
                    {
                        header: 'Type',
                        cell: (org) => TYPE_LABELS[org.type] ?? org.type,
                    },
                    {
                        header: 'Status',
                        cell: (org) => (
                            <span
                                className={
                                    'inline-block rounded-full px-2 py-0.5 text-xs font-medium ' +
                                    (org.status === 'active'
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-gray-100 text-gray-600')
                                }
                            >
                                {org.status}
                            </span>
                        ),
                    },
                    {
                        header: 'Seats',
                        cell: (org) => `${org.seatsUsed} / ${org.seatQuota}`,
                    },
                    {
                        header: 'Coordinators',
                        cell: (org) => org.memberCount,
                    },
                    {
                        header: '',
                        cell: (org) => (
                            <div className='flex items-center justify-end gap-2' onClick={(e) => e.stopPropagation()}>
                                <Link
                                    to={`/admin/organization/${org._id}`}
                                    className='inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-neutral-700 bg-white border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors'
                                >
                                    <PencilSquareIcon className='w-4 h-4' />
                                    Edit &amp; Team
                                </Link>
                                <button
                                    onClick={() => setDeleteId(org._id)}
                                    className='p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors'
                                    title='Delete organization'
                                >
                                    <TrashIcon className='w-4 h-4' />
                                </button>
                            </div>
                        ),
                    },
                ]}
            />

            <ConfirmModal
                open={!!deleteId}
                onCancel={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title='Delete organization'
                description={`Deletes "${deleteTarget?.name ?? 'this organization'}". All coordinator accounts will be disabled and their emails freed up. Family licenses are kept for audit but families revert to their normal access. This cannot be undone.`}
                confirmText='Delete'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}
